import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useDispatch, useSelector } from 'BMapsRedux';
import { App } from 'BMapsSrc/BMapsApp';
import { JobTypes } from 'BMapsSrc/jobs/JobDefinition';
import { updateDisplayedScreeningJobs } from 'BMapsRedux/jobs/access';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import LoadingIconButton from 'BMapsUI/common/LoadingIconButton';
import { fetchJobs } from 'BMapsRedux/jobs/thunks';
import { JobsGrid } from 'BMapsDialog/ImportPane/ImportProteinPane/Tabs/JobsStructuresTab';
import { openScreeningSidePanel } from '../ScreeningSidePanel';

export default function ScreeningJobs({ initData, highlightedJobId, selected }) {
    const { jobs, localJobState, status } = useSelector((state) => state.jobs);
    const connection = App.PrimaryDataConnection;

    const dispatch = useDispatch();
    const retrieveJobs = () => dispatch(fetchJobs());
    const [flashId, setFlashId] = useState(null);
    const [errors, setErrors] = useState([]);
    const screenJobs = useMemo(
        () => jobs.filter((job) => job.jobtype === JobTypes.LIBRARY_SCREEN), [jobs]
    );
    const loadJobsList = async () => {
        const { error } = await retrieveJobs();
        if (error) {
            setErrors([error]);
        } else {
            setErrors([]);
        }
    };

    // Auto-fetch jobs on mount if not already loaded
    useEffect(() => {
        if (status === 'unloaded') {
            loadJobsList();
        }
    }, [status]);

    // Animates the flash when a new job is created, so that it can be easily visible
    useEffect(() => {
        if (!highlightedJobId) return () => null;
        setFlashId(highlightedJobId);

        const timer = setTimeout(() => setFlashId(null), 500);
        return () => {
            clearTimeout(timer);
        };
    }, [highlightedJobId]);

    function setActiveJobs(newJobs) {
        updateDisplayedScreeningJobs(newJobs);
        openScreeningSidePanel('screen_results_tab', { ...initData });
    }

    if (!selected) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '100%',
                overflow: 'hidden',
            }}
        >
            <DialogContentSection
                title="Screening Jobs"
                description="Screening Jobs"
                disablePaddingTop
                style={{
                    marginBottom: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
                titleIcon={(
                    <BMapsTooltip title="Refresh Jobs List">
                        <LoadingIconButton Icon={RefreshIcon} load={loadJobsList} />
                    </BMapsTooltip>
                )}
                contentStyle={{
                    display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
                }}
            >
                <JobsGrid
                    jobs={screenJobs}
                    userSpecifiedSelectivity="individual"
                    dataConnection={connection}
                    flashId={flashId}
                    localJobState={localJobState}
                    retrieveJobs={retrieveJobs}
                    errors={errors}
                    setActiveJobs={setActiveJobs}
                    dataGridProps={{
                        rowHeight: 60,
                        columnVisibilityModel: {
                            jobtype: false,
                        },
                    }}
                    columnControl={{
                        exclude: ['delivered'],
                    }}
                />
            </DialogContentSection>
        </Box>
    );
}
